import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../api/api';
import { ShieldCheck, Warning, Clock, ArrowRight, SealCheck } from '@phosphor-icons/react';

/**
 * VerificationStatusBanner — small contractor-facing banner that shows on
 * the dashboard when the contractor is either unverified, pending, rejected,
 * or recently approved. Renders nothing once it's an old approval.
 */
export default function VerificationStatusBanner() {
  const [status, setStatus] = useState(null); // null | 'none' | 'pending' | 'rejected' | 'approved' | 'withdrawn'
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axiosInstance.get('/verifications/me');
        if (cancelled) return;
        const sub = r.data?.verification;
        setVerified(!!r.data?.verified);
        if (!sub) setStatus(r.data?.verified ? 'approved' : 'none');
        else {
          setStatus(sub.status);
          setReason(sub.reviewer_note || '');
        }
      } catch {
        // silent — banner just hides
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;

  // Already verified AND no recent action — render a tiny "Verified Pro" reassurance bar
  if (verified && status === 'approved') {
    return (
      <div
        className="mb-6 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500 rounded-xl p-3 flex items-center gap-3 shadow-[3px_3px_0px_#10b981]"
        data-testid="verification-banner-approved"
      >
        <div className="w-10 h-10 bg-emerald-500 rounded-lg border-2 border-black flex items-center justify-center flex-shrink-0">
          <SealCheck size={22} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Verified Pro</p>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Your license and insurance are verified — your profile shows the badge.</p>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div
        className="mb-6 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-500 rounded-xl p-3 flex items-center gap-3 shadow-[3px_3px_0px_#F59E0B]"
        data-testid="verification-banner-pending"
      >
        <div className="w-10 h-10 bg-amber-500 rounded-lg border-2 border-black flex items-center justify-center flex-shrink-0">
          <Clock size={22} weight="bold" className="text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-700 dark:text-amber-400">Verification Pending</p>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">We're reviewing your license and insurance. Usually 1-2 business days.</p>
        </div>
        <Link
          to="/verification"
          className="flex-shrink-0 h-9 px-3 bg-white text-amber-900 text-xs font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] flex items-center gap-1"
          data-testid="verification-banner-view-btn"
        >
          View <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    );
  }

  if (status === 'rejected') {
    return (
      <div
        className="mb-6 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-xl p-3 flex items-center gap-3 shadow-[3px_3px_0px_#EF4444]"
        data-testid="verification-banner-rejected"
      >
        <div className="w-10 h-10 bg-red-500 rounded-lg border-2 border-black flex items-center justify-center flex-shrink-0">
          <Warning size={22} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest text-red-700 dark:text-red-400">Submission Not Approved</p>
          <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate">{reason || 'Please review the feedback and re-submit.'}</p>
        </div>
        <Link
          to="/verification"
          className="flex-shrink-0 h-9 px-3 bg-red-500 text-white text-xs font-black uppercase rounded-lg border-2 border-black shadow-[2px_2px_0px_#000]"
          data-testid="verification-banner-resubmit-btn"
        >
          Re-submit
        </Link>
      </div>
    );
  }

  // none or withdrawn → soft CTA
  return (
    <div
      className="mb-6 bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 border-2 border-black rounded-xl p-3 flex items-center gap-3 shadow-[3px_3px_0px_#000]"
      data-testid="verification-banner-cta"
    >
      <div className="w-10 h-10 bg-emerald-500 rounded-lg border-2 border-black flex items-center justify-center flex-shrink-0">
        <ShieldCheck size={22} weight="fill" className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Become a Verified Pro</p>
        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Upload your license + insurance to earn the badge. Homeowners book Verified Pros 3× more often.
        </p>
      </div>
      <Link
        to="/verification"
        className="flex-shrink-0 h-9 px-3 bg-emerald-500 text-black text-xs font-black uppercase tracking-wider rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] flex items-center gap-1"
        data-testid="verification-banner-start-btn"
      >
        Start <ArrowRight size={14} weight="bold" />
      </Link>
    </div>
  );
}
